import { Box, Icon, Typography } from "@outschool/backpack";
import { faChevronDown } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

type ShowMoreProps = {
  hasResults: boolean;
  hasTimeFilter: boolean;
  onClick?: () => void;
  disabled?: boolean;
};

export default function ShowMore({
  hasResults,
  hasTimeFilter,
  onClick,
  disabled
}: ShowMoreProps) {
  const isMobile = useIsMobile();
  const { t } = useTranslation("client\\components\\search\\ShowMore");
  const showButton = !!onClick && !disabled;

  let heading, text;
  if (hasResults) {
    if (hasTimeFilter) {
      heading = t("Some matching classes don’t fit your schedule");
      text = t(
        "The classes below don’t fit your time filters or are by request."
      );
    } else {
      heading = t("Some matching classes aren’t currently available");
      text = t("The classes below are not scheduled yet or are by request.");
    }
  } else {
    if (hasTimeFilter) {
      heading = t("No matching classes fit your schedule");
      text = t(
        "The classes below don’t fit your time filters or are by request."
      );
    } else {
      heading = t("No matching classes are currently available");
      text = t("The classes below are not scheduled yet or are by request.");
    }
  }

  return (
    <Box
      sx={{
        padding: "0.75em 0",
        textAlign: "center",
        minHeight: isMobile ? "30rem" : "24rem",
        width: "100%"
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingY: "1em",
          paddingX: isMobile ? "1em" : undefined
        }}
      >
        <Typography
          variant="h3"
          sx={{
            paddingY: "0.5em"
          }}
        >
          {heading}
        </Typography>
        <Box
          sx={{
            maxWidth: isMobile ? "30rem" : undefined
          }}
        >
          {text}
          {!isMobile && <br />}{" "}
          {t("You can ask the teacher to schedule a time for you.")}
        </Box>
      </Box>
      {showButton ? (
        <Box
          sx={{
            "&::before": {
              position: "absolute",
              content: '""',
              height: "4rem",
              width: "100%",
              left: 0,
              zIndex: -1,
              background:
                "linear-gradient(rgba(245, 245, 245, 0), rgba(245, 245, 245, 1))"
            }
          }}
        >
          <TrackedButton
            variant="contained"
            onClick={onClick}
            sx={{
              marginTop: "1em"
            }}
            trackingName="activity_listings_show_more"
            trackingSharedProperties={{
              type: hasResults ? "end_of_results" : "no_results"
            }}
          >
            <Box component="span">
              <Icon icon={faChevronDown} />
              <Box
                component="span"
                sx={{
                  padding: "0 1.5rem"
                }}
              >
                {t("Show more results")}
              </Box>
              <Icon icon={faChevronDown} />
            </Box>
          </TrackedButton>
        </Box>
      ) : (
        <Box
          sx={{
            height: "4rem",
            width: "100%",
            borderBottom: "1px solid",
            borderColor: "grey.100",
            color: "grey.900"
          }}
        />
      )}
    </Box>
  );
}
